import { defaultSchema } from "rehype-sanitize";

// Everything the article renderer needs to stay faithful to the source Markdown
// while refusing to execute anything an author (or a compromised row) wrote.

export const anchorPrefix = "md-";

const allowedEmbedHosts = [
  "www.youtube-nocookie.com",
  "www.youtube.com",
  "youtube.com",
  "player.vimeo.com",
  "codesandbox.io",
  "codepen.io",
  "stackblitz.com",
  "gist.github.com",
  "open.spotify.com",
  "w.soundcloud.com"
];

const imageDataUri = /^data:image\/(png|jpeg|gif|webp|avif|svg\+xml);base64,[a-z0-9+/=]+$/i;
const safeSchemes = ["http", "https", "mailto", "tel"];

/** hast is plain JSON; this is the slice of it these plugins touch. */
type Node = {
  type: string;
  tagName?: string;
  value?: string;
  properties?: Record<string, unknown>;
  children?: Node[];
};

function walk(node: Node, visit: (node: Node, parent: Node | null) => boolean | void, parent: Node | null = null) {
  if (visit(node, parent) === false) return;
  for (const child of [...(node.children ?? [])]) walk(child, visit, node);
}

function removeChild(parent: Node | null, node: Node) {
  if (!parent?.children) return;
  const index = parent.children.indexOf(node);
  if (index >= 0) parent.children.splice(index, 1);
}

/** Plain text of a node, used for code fences (copy button, Mermaid source). */
export function nodeText(node: unknown): string {
  const current = node as Node | null | undefined;
  if (!current) return "";
  if (current.type === "text") return current.value ?? "";
  return (current.children ?? []).map(nodeText).join("");
}

export function codeLanguage(className: unknown): string {
  const classes = Array.isArray(className) ? className : String(className ?? "").split(/\s+/);
  for (const value of classes) {
    const match = /^language-([a-z0-9+#.-]+)$/i.exec(String(value));
    if (match) return match[1].toLowerCase();
  }
  return "";
}

/**
 * The sanitiser drops anything that could run. Class names are kept because
 * KaTeX, highlighting, footnotes and task lists all travel as classes.
 */
export const markdownSchema = (() => {
  const schema = structuredClone(defaultSchema);
  const attributes = { ...(schema.attributes ?? {}) };
  const globals = [...(attributes["*"] ?? []), "className", "id", "ariaHidden", "ariaLabel", "role"];

  schema.clobberPrefix = anchorPrefix;
  schema.tagNames = [
    ...new Set([
      ...(schema.tagNames ?? []),
      "abbr", "audio", "caption", "col", "colgroup", "details", "dfn", "figcaption", "figure",
      "iframe", "mark", "picture", "small", "source", "summary", "time", "track", "u", "video", "wbr"
    ])
  ];
  schema.attributes = {
    ...attributes,
    "*": globals,
    a: [...(attributes.a ?? []), "rel", "target", "download"],
    img: [...(attributes.img ?? []), "alt", "src", "title", "width", "height", "loading", "decoding", "srcSet", "sizes"],
    iframe: ["src", "title", "width", "height", "allow", "allowFullScreen", "loading", "referrerPolicy"],
    video: ["src", "controls", "poster", "width", "height", "muted", "loop", "playsInline", "preload"],
    audio: ["src", "controls", "loop", "preload"],
    source: ["src", "srcSet", "type", "media", "sizes"],
    track: ["src", "kind", "srcLang", "label", "default"],
    details: ["open"],
    ol: [...(attributes.ol ?? []), "start", "reversed", "type"],
    td: [...(attributes.td ?? []), "align", "colSpan", "rowSpan"],
    th: [...(attributes.th ?? []), "align", "colSpan", "rowSpan", "scope"]
  };
  schema.protocols = {
    ...(schema.protocols ?? {}),
    href: ["http", "https", "mailto", "tel", "irc", "ircs", "xmpp"],
    src: ["http", "https", "data"],
    cite: ["http", "https"],
    poster: ["http", "https", "data"]
  };
  return schema;
})();

/**
 * Runs after sanitising: keeps in-page links pointing at the prefixed ids the
 * sanitiser produced, hardens outbound links, and allows embeds only from a
 * short list of hosts over https (a `data:` iframe has no host, so it goes).
 */
export function rehypeArticlePolish() {
  return (tree: unknown) => {
    walk(tree as Node, (node, parent) => {
      if (node.type !== "element" || !node.properties) return;
      const properties = node.properties;

      if (node.tagName === "a" && typeof properties.href === "string") {
        const href = properties.href;
        if (href.startsWith("#") && !href.startsWith(`#${anchorPrefix}`)) properties.href = `#${anchorPrefix}${href.slice(1)}`;
        if (/^https?:/i.test(href)) {
          properties.target = "_blank";
          properties.rel = "noreferrer noopener";
        }
      }

      if (node.tagName === "img") {
        properties.loading ??= "lazy";
        properties.decoding ??= "async";
      }

      if (node.tagName === "iframe") {
        let host = "";
        try {
          const url = new URL(String(properties.src ?? ""), "https://invalid.invalid");
          host = url.protocol === "https:" ? url.hostname.toLowerCase() : "";
        } catch {
          host = "";
        }
        if (!allowedEmbedHosts.includes(host)) {
          removeChild(parent, node);
          return false;
        }
        properties.loading ??= "lazy";
        properties.referrerPolicy ??= "no-referrer";
        properties.allow ??= "accelerometer; clipboard-write; encrypted-media; picture-in-picture; fullscreen";
      }
    });
  };
}

/** react-markdown asks about every url before it reaches the DOM. */
export function transformUrl(url: string, key: string, node: unknown) {
  const value = (url ?? "").trim();
  const tagName = (node as Node | null)?.tagName;
  if ((tagName === "img" || tagName === "source") && key === "src" && imageDataUri.test(value)) return value;
  const scheme = /^([a-z][a-z0-9+.-]*):/i.exec(value);
  // No scheme means a relative path or an in-page anchor, which are always fine.
  if (!scheme) return value;
  return safeSchemes.includes(scheme[1].toLowerCase()) ? value : "";
}

export const embedHosts = allowedEmbedHosts;
